import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReferenceInput, AutocompleteInput, FormDataConsumer } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { IconButton } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import dataProvider from '../../providers/dataProvider';
import VideoPickerButton from '../../components/VideoPickerButton';

const formatOptionText = (choice) => {
  if (!choice) {
    return '';
  }

  const title = choice.originalName || 'Untitled video';
  const muxId = choice.muxPlaybackId ? ` • ${choice.muxPlaybackId}` : '';

  return `${title}${muxId}`;
};

const filterToQuery = (searchText) => ({
  search: searchText || undefined,
});

const alwaysRenderSuggestions = (inputValue) => !!inputValue && inputValue.trim().length > 0;

const VideoPreview = ({ video }) => {
  if (!video) {
    return null;
  }

  return (
    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center' }}>
      <div>
        <div style={{ fontWeight: 600 }}>{video.originalName || 'Selected video'}</div>
        {video.muxPlaybackId ? (
          <div style={{ fontSize: 12 }}>Mux ID: {video.muxPlaybackId}</div>
        ) : null}
      </div>
      {video.muxPlaybackId ? (
        <IconButton
          href={`https://stream.mux.com/${video.muxPlaybackId}/low.mp4`}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          title="Open stream in new tab"
        >
          <LaunchIcon fontSize="small" />
        </IconButton>
      ) : null}
    </div>
  );
};

const VideoPreviewFromValues = ({ showPreview }) => (
  <FormDataConsumer>
    {({ formData }) => (
      <VideoPreviewContent
        showPreview={showPreview}
        selectedVideo={formData?.video}
        selectedVideoId={formData?.videoId}
      />
    )}
  </FormDataConsumer>
);

const VideoPreviewContent = ({ showPreview, selectedVideo, selectedVideoId }) => {
  const [previewVideo, setPreviewVideo] = useState(selectedVideo || null);
  const lastLoadedId = useRef(selectedVideo?.id || null);

  useEffect(() => {
    let isMounted = true;
    const cleanup = () => {
      isMounted = false;
    };

    if (!showPreview) {
      return cleanup;
    }

    if (selectedVideo && selectedVideo.id !== lastLoadedId.current) {
      setPreviewVideo(selectedVideo);
      lastLoadedId.current = selectedVideo.id;
      return cleanup;
    }

    if (!selectedVideoId) {
      if (previewVideo) {
        setPreviewVideo(null);
      }
      lastLoadedId.current = null;
      return cleanup;
    }

    if (lastLoadedId.current === selectedVideoId) {
      return cleanup;
    }

    lastLoadedId.current = selectedVideoId;
    dataProvider
      .getOne('file/video', { id: selectedVideoId })
      .then(({ data }) => {
        if (isMounted) {
          setPreviewVideo(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setPreviewVideo(null);
        }
      });

    return cleanup;
  }, [showPreview, selectedVideo, selectedVideoId, previewVideo]);

  if (!showPreview || !previewVideo) {
    return null;
  }

  return <VideoPreview video={previewVideo} />;
};

const AchievementVideoInput = ({ helperText, showPreview = true }) => {
  const formContext = useFormContext();

  const handleSelectFromDialog = useCallback(
    (video) => {
      if (formContext?.setValue) {
        formContext.setValue('videoId', video?.id || null);
      }
    },
    [formContext],
  );

  return (
    <>
      <ReferenceInput source="videoId" reference="file/video" perPage={25} label="Associated Video">
        <AutocompleteInput
          optionText={formatOptionText}
          optionValue="id"
          helperText={helperText}
          filterToQuery={filterToQuery}
          shouldRenderSuggestions={alwaysRenderSuggestions}
          noOptionsText="No videos found"
          fullWidth
          placeholder="Search videos..."
        />
      </ReferenceInput>
      <VideoPickerButton onSelect={handleSelectFromDialog} style={{ marginTop: 8 }} />
      <VideoPreviewFromValues showPreview={showPreview} />
    </>
  );
};

export default AchievementVideoInput;
